import { useState, useRef } from "react";
import { Box, Button, TextField, useTheme } from "@mui/material";
import FileUpload, { ChatFile, DragDropZone, FileUploadRef } from "./controls/FileUpload";

export interface PromptProps {
  loading: boolean;
  onSendMessage: (message: string, files?: ChatFile[]) => void;
  onStop: () => void;
}
function PromptSection({
  loading,
  onSendMessage,
  onStop
}: PromptProps) {
  const [input, setInput] = useState<string>("");
  const [attachedFiles, setAttachedFiles] = useState<ChatFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileUploadRef = useRef<FileUploadRef>(null);

  const handleSubmit = () => {
    if (loading) {
      onStop();
    } else {
      onSendMessage(input, attachedFiles);
      setInput("");
      // Clear attached files after sending
      setAttachedFiles([]);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleFilesDropped = (fileList: FileList) => {
    // Call the FileUpload's processFiles method
    fileUploadRef.current?.processFiles(fileList);
  };

  const handlePaste = async (event: React.ClipboardEvent) => {
    const clipboardData = event.clipboardData;
    
    if (!clipboardData) return;

    // Check if clipboard contains files (images)
    const items = clipboardData.items;
    const files: File[] = [];
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Check if the item is an image
      if (item.type.startsWith('image/')) {
        event.preventDefault(); // Prevent pasting the image as text
        
        const file = item.getAsFile();
        if (file) {
          // Rename the file to something meaningful with timestamp
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
          const extension = file.type.split('/')[1] || 'png';
          const renamedFile = new File([file], `pasted-image-${timestamp}.${extension}`, {
            type: file.type,
          });
          files.push(renamedFile);
        }
      }
    }

    // Process the pasted image files
    if (files.length > 0) {
      const fileList = new DataTransfer();
      files.forEach(file => fileList.items.add(file));
      fileUploadRef.current?.processFiles(fileList.files);
    }
  };

  return (
    <DragDropZone
      onFilesDropped={handleFilesDropped}
      isDragging={isDragging}
      onDragStateChange={setIsDragging}
    >
      <Box component="div">
      <TextField
        multiline
        fullWidth
        minRows={3}
        maxRows={10}
        variant="outlined"
        placeholder="Type your message here (or paste images)..."
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyPress}
        onPaste={handlePaste}
        sx={{
          mb: 2,
          borderRadius: 2,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              border: '1px solid rgba(255, 255, 255, 0.1)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
          },
        }}
      />
      
      {/* File Upload Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <FileUpload
          ref={fileUploadRef}
          files={attachedFiles}
          onFilesChange={setAttachedFiles}
          maxFiles={5}
          maxSizeMB={20}
          onDragStateChange={setIsDragging}
        />
        <Box sx={{ flex: 1 }} />
        <Button
          type="button"
          variant="contained"
          onClick={handleSubmit}
          color={loading ? 'secondary' : 'primary'}
          disabled={(!input.trim() && attachedFiles.length === 0) && !loading}
          sx={{
            borderRadius: 2,
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
          }}
        >
          {loading ? 'Stop' : 'Send'}
        </Button>
      </Box>
    </Box>
    </DragDropZone>
  );
}
export default PromptSection;